// =============================================================================
// usePortalNotifyCountdown — global countdown bar state for portal notifications
// =============================================================================
// PURPOSE: Manages the single active countdown for a pending portal task
//          notification. Pages call countdown.start() after a notifying action
//          (create, publish, move, complete, edit). The bar ticks 30→0 and hides
//          at 0. [Cancel] deletes the pending row. [Preview] expands plain-text
//          summary. The countdown does NOT post to Slack — the server (pg_cron)
//          owns the fire. This hook is the MIRROR + Cancel.
//
// MULTI-TAB: Subscribes to client_task_pending_notifications realtime filtered
//            to the active task_id. On DELETE → bar clears (another tab canceled).
//            On INSERT → bar restarts (comment re-enqueue from another session).
//
// CLEAR-ON-CONTEXT-CHANGE: On session or organizationId change → clear() without
//                           calling cancel (the member-gate RPC would throw cross-
//                           org). Server fires within the 30s–5min window anyway.
// =============================================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { showToast } from "../components/portal/portalHelpers";

// Notify countdown window. PROD value = 5 min (300s). MUST stay in sync with the
// `fire_at = now() + interval '5 minutes'` literal in the DB enqueue fns
// (portal_task_enqueue_notification + portal_comment_enqueue_notification).
// Widened 30s → 5min Jul 12 to coalesce a burst of edits into ONE Slack card —
// see migration 2026-07-12_portal_notify_5min_batch.sql.
// (Was 5s Jun 4–6 during testing; 30s Jun 7 → 5min Jul 12.)
// NB: the DB caps fire_at at first_event+5min, so on a multi-edit burst the real
// send may land slightly before this bar hits zero — cosmetic, "Send now" still works.
export const PORTAL_NOTIFY_WINDOW_SECONDS = 300;
export const PORTAL_NOTIFY_WINDOW_MS = PORTAL_NOTIFY_WINDOW_SECONDS * 1000;

export interface CountdownEntry {
  taskId: string;
  label: string; // e.g. "created a task", "published a task", "moved a task"
  fireAtMs: number; // Date.now() + PORTAL_NOTIFY_WINDOW_MS when start() is called
}

export interface CountdownHandle {
  active: CountdownEntry | null;
  secondsLeft: number;
  previewOpen: boolean;
  start: (entry: CountdownEntry) => void;
  clear: () => void; // hide bar, do NOT cancel on server
  cancel: () => Promise<void>; // DELETE server row + hide bar
  sendNow: () => Promise<void>; // fire the Slack card NOW (skip countdown) + hide bar
  togglePreview: () => void;
}

export function usePortalNotifyCountdown(organizationId: string | null): CountdownHandle {
  const { user } = useAuth();
  const sessionId = user?.id ?? null;

  const [active, setActive] = useState<CountdownEntry | null>(null);
  // Prod: 5min window. Matches PORTAL_NOTIFY_WINDOW_MS + the DB fire_at interval
  // in the enqueue fns. (5s Jun 4–6 testing; 30s Jun 7; 5min Jul 12 for batching.)
  const [secondsLeft, setSecondsLeft] = useState(PORTAL_NOTIFY_WINDOW_SECONDS);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Interval ref for the tick
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Guards a double-click on [Send now]: clear() nulls `active` asynchronously,
  // so the second click's closure still sees a non-null active. The server's
  // per-task lock + DELETE-before-POST make a double-fire harmless, but this
  // skips the wasted second RPC round-trip.
  const sendingRef = useRef(false);

  // Clear the tick interval
  const stopTick = useCallback(() => {
    if (tickRef.current !== null) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  // Hide bar locally without touching server
  const clear = useCallback(() => {
    stopTick();
    setActive(null);
    setSecondsLeft(30);
    setPreviewOpen(false);
  }, [stopTick]);

  // Start (or restart) for a given entry. Resets to 30s.
  const start = useCallback(
    (entry: CountdownEntry) => {
      stopTick();
      setPreviewOpen(false);
      setActive(entry);

      const computeSecondsLeft = () => {
        const ms = entry.fireAtMs - Date.now();
        return Math.max(0, Math.ceil(ms / 1000));
      };

      setSecondsLeft(computeSecondsLeft());

      tickRef.current = setInterval(() => {
        const s = computeSecondsLeft();
        setSecondsLeft(s);
        if (s <= 0) {
          // Timer elapsed — server will have fired; hide the bar.
          stopTick();
          setActive(null);
          setPreviewOpen(false);
        }
      }, 1000);
    },
    [stopTick]
  );

  // Cancel: DELETE the server row, then clear the bar
  const cancel = useCallback(async () => {
    if (!active) return;
    const taskId = active.taskId;
    clear();
    try {
      const { error } = await supabase!.rpc("cancel_portal_task_notification", {
        p_task_id: taskId,
      });
      if (error) throw error;
      showToast("Notification canceled", "success");
    } catch (err) {
      // Cancel errors are non-fatal — the server will fire within 30s–5min
      console.warn("[countdown] cancel RPC failed:", err);
    }
  }, [active, clear]);

  // Send now: fire the Slack card immediately (server dispatches THIS task,
  // bypassing the fire_at countdown), then clear the bar. On failure the pending
  // row is untouched, so the cron still fires it within the normal window.
  const sendNow = useCallback(async () => {
    if (!active || sendingRef.current) return;
    sendingRef.current = true;
    const taskId = active.taskId;
    clear();
    try {
      const { error } = await supabase!.rpc("send_portal_task_notification_now", {
        p_task_id: taskId,
      });
      if (error) throw error;
      showToast("Notification sent", "success");
    } catch (err) {
      // Non-fatal — pending row stays, cron fires it within the 30s–5min window
      console.warn("[countdown] send-now RPC failed:", err);
      showToast("Couldn't send now — it'll still send shortly", "error");
    } finally {
      sendingRef.current = false;
    }
  }, [active, clear]);

  const togglePreview = useCallback(() => {
    setPreviewOpen((v) => !v);
  }, []);

  // ---- Clear on session or org change (no cancel call — member-gate would throw) ----
  const prevSessionRef = useRef(sessionId);
  const prevOrgRef = useRef(organizationId);

  useEffect(() => {
    const sessionChanged = prevSessionRef.current !== sessionId;
    const orgChanged = prevOrgRef.current !== organizationId;
    prevSessionRef.current = sessionId;
    prevOrgRef.current = organizationId;

    if ((sessionChanged || orgChanged) && active) {
      // Silently clear — let the server fire/expire. Do NOT call cancel.
      stopTick();
      setActive(null);
      setSecondsLeft(30);
      setPreviewOpen(false);
    }
  }, [sessionId, organizationId, active, stopTick]);

  // ---- Realtime: subscribe to client_task_pending_notifications for active task ----
  useEffect(() => {
    if (!active?.taskId || !supabase) return;

    const taskId = active.taskId;
    const channel = supabase
      .channel(`portal-notify-${taskId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "client_task_pending_notifications",
          filter: `task_id=eq.${taskId}`,
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            // Another tab (or server) removed the row — clear bar + notify
            stopTick();
            setActive(null);
            setSecondsLeft(30);
            setPreviewOpen(false);
            showToast("Notification canceled", "success");
          } else if (payload.eventType === "INSERT") {
            // Row re-inserted (e.g. comment enqueue after a cancel from another tab)
            // Re-start the bar from the fire_at the server stamped.
            const record = payload.new as { fire_at?: string };
            const fireAtMs = record.fire_at
              ? new Date(record.fire_at).getTime()
              : Date.now() + PORTAL_NOTIFY_WINDOW_MS;
            start({ taskId, label: active?.label ?? "updated a task", fireAtMs });
          }
        }
      )
      .subscribe();

    return () => {
      void supabase!.removeChannel(channel);
    };
  }, [active?.taskId, active?.label, start, stopTick]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTick();
    };
  }, [stopTick]);

  return { active, secondsLeft, previewOpen, start, clear, cancel, sendNow, togglePreview };
}
